import { Divider, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfAppDay, startOfAppDay } from "@repo/utils/date";
import type { PrismaFilter, WhereFilter } from "@types_def/common/filter.types";
import type { Delivery } from "@types_def/models/delivery.types";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { fromPickerDate, toPickerDate } from "@/utils/picker-date";

/** A picked day as the first (`from`) or last (`to`) instant of that Paris day. */
function toDayBound(value: Date | null, from: boolean): Date | null {
  const instant = fromPickerDate(value);
  if (!instant) return null;
  return from ? startOfAppDay(instant) : endOfAppDay(instant);
}

function DeliveryDeadlineDayPicker({
  name,
  label,
}: {
  name: "carDeliveryDeadlineFrom" | "carDeliveryDeadlineTo";
  label: string;
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <DatePicker
          label={label}
          value={toPickerDate(field.value as Date | null | undefined)}
          onChange={(value) =>
            field.onChange(toDayBound(value, name === "carDeliveryDeadlineFrom"))
          }
          inputRef={field.ref}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              onBlur: field.onBlur,
            },
          }}
        />
      )}
    />
  );
}

type DeliveryTableDeliveryDateFilterProps = {
  from?: boolean;
  filter?: WhereFilter<Delivery>;
  handleDateChange?: (
    date: Date | undefined | null,
    prop: keyof PrismaFilter<Delivery, "carDeliveryDeadline">
  ) => void;
};

function DeliveryTableDeliveryDateFilter({
  from,
  filter,
  handleDateChange,
}: DeliveryTableDeliveryDateFilterProps) {
  const { t } = useTranslation();

  if (handleDateChange) {
    const filterKey: keyof PrismaFilter<Delivery, "carDeliveryDeadline"> = from ? "gte" : "lte";
    const dateFilter = filter?.carDeliveryDeadline;
    const rawDateValue =
      dateFilter && typeof dateFilter === "object"
        ? Object.entries(dateFilter).find(([key]) => key === filterKey)?.[1]
        : undefined;
    const currentValue =
      typeof rawDateValue === "string" || rawDateValue instanceof Date
        ? toPickerDate(rawDateValue)
        : null;

    return (
      <DatePicker
        label={from ? t("common.shared.from") : t("common.shared.to")}
        onChange={(value) =>
          handleDateChange(toDayBound(value, Boolean(from)) ?? undefined, filterKey)
        }
        slotProps={{
          textField: {
            size: "small",
          },
        }}
        value={currentValue}
      />
    );
  }

  return (
    <>
      <Typography variant="body2" letterSpacing={2} fontWeight={600}>
        {" "}
        {t("common.table.headers.Date de livraison")}
      </Typography>
      <Divider />
      <Stack
        margin={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <DeliveryDeadlineDayPicker name="carDeliveryDeadlineFrom" label={t("common.shared.from")} />
        <DeliveryDeadlineDayPicker name="carDeliveryDeadlineTo" label={t("common.shared.to")} />
      </Stack>
    </>
  );
}

export default DeliveryTableDeliveryDateFilter;
